/**
 * Message Transport Service
 *
 * Handles sending messages via WebSocket with HTTP fallback.
 * Manages duplicate prevention and timeout cleanup.
 */

import {
  GetMessagesResponseItem,
  MessageControllerService,
  SenderMetadataResponse
} from "@9amhealth/openapi";
import { nanoid } from "nanoid";
import dayjs from "dayjs";
import { DateFormats } from "src/lib/date";
import type { WebsocketMessage } from "src/state/WebSocketBloc/WebSocketBloc";
import {
  MESSAGE_METADATA_KEYS,
  WebsocketMessageType
} from "src/state/WebSocketBloc/WebSocketBloc";
import { MESSAGE_RETRY } from "../constants/ChatConstants";
import reportErrorSentry from "src/lib/reportErrorSentry";
import * as SendingMessagesTracker from "../SendingMessagesTracker";
import type { ChatBlocDependencies } from "../types/ChatBlocDependencies";

export interface SendMessageOptions {
  content: string;
  userId: string;
  meta?: Record<string, string | string[] | boolean>;
  parseTextToHtml: (text: string) => string;
  onLocalMessageCreated: (message: GetMessagesResponseItem) => void;
  onMessageSentSuccess: (message: GetMessagesResponseItem | undefined) => void;
  log: (message: string, details?: unknown) => void;
  sendViaHTTP: (payload: {
    contentType: string;
    contentValue?: string;
    metadata?: AnyObject;
  }) => Promise<void>;
}

/**
 * MessageTransport handles the dual-channel message sending:
 * 1. Primary: WebSocket for real-time delivery
 * 2. Fallback: HTTP POST when WebSocket fails or is unavailable
 *
 * Features:
 * - Automatic fallback from WebSocket to HTTP
 * - Duplicate prevention tracking
 * - Timeout management for in-flight messages
 * - Error handling and reporting
 */
export class MessageTransport {
  /**
   * Tracks messages currently being sent via HTTP to prevent duplicate sends.
   * Maps local message ID to timeout for cleanup.
   */
  private sendingMessages: Map<string, ReturnType<typeof setTimeout>> =
    new Map();

  /**
   * Tracks messages currently being sent via WebSocket to prevent duplicate sends.
   * Maps local message ID to a promise that resolves when send completes.
   */
  private sendingViaWebSocket: Map<string, Promise<void>> = new Map();

  constructor(
    private deps: ChatBlocDependencies,
    private messageController: typeof MessageControllerService
  ) {}

  /**
   * Send a message with automatic WebSocket -> HTTP fallback
   *
   * HOTFIX: Currently bypassing WebSocket, sending via HTTP only.
   * To revert: Remove the early HTTP send block below.
   */
  async sendMessage(options: SendMessageOptions): Promise<void> {
    const {
      content,
      userId,
      meta = {},
      parseTextToHtml,
      onLocalMessageCreated,
      log,
      sendViaHTTP
    } = options;

    const localId = `local_${nanoid(5)}`;
    meta[MESSAGE_METADATA_KEYS.LOCAL_ID] = localId;

    const timeStamp = dayjs().format(DateFormats.ISO_FULL);
    meta[MESSAGE_METADATA_KEYS.SEND_TIMESTAMP] = timeStamp;

    const asMessageResponseItem = {
      id: localId,
      senderId: userId,
      messageOwnerId: userId,
      senderMetadata: {
        role: SenderMetadataResponse.role.USER
      },
      messageReceiveTimestamp: timeStamp,
      contentType: "text/html",
      contentValue: content ? parseTextToHtml(content) : "",
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      metadata: meta as Record<string, any>,
      senderType: GetMessagesResponseItem.senderType.USER
    } satisfies GetMessagesResponseItem;

    const wsMessage = {
      type: WebsocketMessageType.sendMessage,
      payload: {
        contentType: asMessageResponseItem.contentType,
        contentValue: asMessageResponseItem.contentValue,
        metadata: asMessageResponseItem.metadata
      }
    } satisfies WebsocketMessage;

    // HOTFIX: Use HTTP for all sends (bypass WebSocket)
    // TO REVERT: Remove this block to restore WebSocket-first behavior
    try {
      onLocalMessageCreated(asMessageResponseItem);
    } catch (e) {
      log("Failed to create local message", { error: e, localId });
      reportErrorSentry(
        new Error("Creating Local Message Error", { cause: e })
      );
    }

    // Check if message is already being sent via HTTP to prevent duplicates
    if (this.sendingMessages.has(localId)) {
      log("Message already being sent via HTTP, skipping", { localId });
      return;
    }

    // Mark message as sending and set timeout for cleanup
    const timeout = setTimeout(() => {
      this.sendingMessages.delete(localId);
      log("Message sending timeout expired, cleaned up state", { localId });
    }, MESSAGE_RETRY.SEND_TIMEOUT_MS);

    this.sendingMessages.set(localId, timeout);

    try {
      await sendViaHTTP(wsMessage.payload);

      // On success, clear the timeout and remove from sending state immediately
      const existingTimeout = this.sendingMessages.get(localId);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }
      this.sendingMessages.delete(localId);
      return; // Exit early - hotfix complete
    } catch (error) {
      // On failure, clear sending state immediately so retry can happen
      const existingTimeout = this.sendingMessages.get(localId);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }
      this.sendingMessages.delete(localId);
      reportErrorSentry(
        new Error("Sending Message HOTFIX HTTP Error", { cause: error })
      );
      throw error;
    }
    // --- END HOTFIX ---

    // --- Original WebSocket code below (preserved, unreachable) ---
    try {
      onLocalMessageCreated(asMessageResponseItem);
      await this.deps.websocket.send(wsMessage);
    } catch (e) {
      this.deps.websocket.removeLocalMessage(wsMessage);

      // Check if message is already being sent via HTTP to prevent duplicates
      if (this.sendingMessages.has(localId)) {
        log("Message already being sent via HTTP, skipping", { localId });
        return;
      }

      // Mark message as sending and set timeout for cleanup
      const timeout = setTimeout(() => {
        this.sendingMessages.delete(localId);
        log("Message sending timeout expired, cleaned up state", { localId });
      }, MESSAGE_RETRY.SEND_TIMEOUT_MS);

      this.sendingMessages.set(localId, timeout);

      try {
        await sendViaHTTP(wsMessage.payload);

        // On success, clear the timeout and remove from sending state immediately
        // No delay needed - the global tracker prevents duplicates
        const existingTimeout = this.sendingMessages.get(localId);
        if (existingTimeout) {
          clearTimeout(existingTimeout);
        }
        this.sendingMessages.delete(localId);
      } catch (error) {
        // On failure, clear sending state immediately so retry can happen
        const existingTimeout = this.sendingMessages.get(localId);
        if (existingTimeout) {
          clearTimeout(existingTimeout);
        }
        this.sendingMessages.delete(localId);
        throw error;
      }

      reportErrorSentry(new Error("Sending Message WS Error", { cause: e }));
    }
  }

  /**
   * Send a message via HTTP POST (fallback method)
   */
  async sendMessageViaHTTP(options: {
    payload: {
      contentType: string;
      contentValue?: string;
      metadata?: AnyObject;
    };
    userId: string;
    onMessageSentSuccess: (
      message: GetMessagesResponseItem | undefined
    ) => void;
    log: (message: string, details?: unknown) => void;
  }): Promise<void> {
    const { payload, userId, onMessageSentSuccess, log } = options;

    log("Sending message with POST");

    if (!userId) {
      reportErrorSentry(new Error("Sending Message No User"));
      return;
    }

    // Extract local-id for duplicate prevention
    const localId = String(
      payload.metadata?.[MESSAGE_METADATA_KEYS.LOCAL_ID] || ""
    );

    // Check if this message has already been sent
    if (localId && SendingMessagesTracker.hasBeenSent(localId)) {
      log(`Message already sent (global tracker), skipping HTTP send:`, {
        localId
      });
      return;
    }

    // Mark message as sent - once marked, it will never be sent again
    if (localId && !SendingMessagesTracker.markAsSent(localId)) {
      log(
        `Message already marked as sent (race condition), skipping HTTP send:`,
        { localId }
      );
      return;
    }

    try {
      const res = await this.messageController.postToOwnersLifeline(userId, {
        metadata: payload.metadata,
        contentType: payload.contentType,
        contentValue: payload.contentValue
      });
      onMessageSentSuccess(res.data.storedMessage);
    } catch (e) {
      this.deps.notifications.showError("error_websocket_not_connected");
      reportErrorSentry(new Error("Sending Message POST Error", { cause: e }));
      // Don't re-throw - the error is already reported and toasted
      // Re-throwing would break existing code that expects this method to not throw
    }
  }

  /**
   * Check if a message is currently being sent via HTTP or WebSocket
   */
  isSending(localId: string): boolean {
    return (
      this.sendingMessages.has(localId) || this.sendingViaWebSocket.has(localId)
    );
  }

  /**
   * Send a message via WebSocket with tracking to prevent duplicates
   */
  async sendViaWebSocket(
    wsMessage: WebsocketMessage,
    localId: string,
    log: (message: string, details?: unknown) => void
  ): Promise<void> {
    // Check if already sending this message
    if (this.sendingViaWebSocket.has(localId)) {
      log("Message already being sent via WebSocket, skipping", { localId });
      return;
    }

    // Create promise for this send operation
    const sendPromise = (async () => {
      try {
        await this.deps.websocket.send(wsMessage);
        log("Message sent successfully via WebSocket", { localId });
      } finally {
        // Clean up tracking after send completes (success or failure)
        this.sendingViaWebSocket.delete(localId);
      }
    })();

    // Track this send operation
    this.sendingViaWebSocket.set(localId, sendPromise);

    // Wait for completion
    await sendPromise;
  }

  /**
   * Clear all sending state (used during user logout/clear)
   */
  clearSendingState(): void {
    for (const timeout of this.sendingMessages.values()) {
      clearTimeout(timeout);
    }
    this.sendingMessages.clear();
    this.sendingViaWebSocket.clear();
  }
}
